import React, { useEffect, useState } from "react";
import Header from "../components/shared/header";
import { useParams, Link } from "react-router-dom";
import { manualBlogs } from "../core/api/data";
import { formatDate } from "../core/utils/blog";
import { RxCaretRight } from "react-icons/rx";
import { fetchArticleBySlug } from "../core/api/rss";
import BlogSkel from "../components/blog/skeleton/BlogSkel";
import About from "../components/shared/about";
import Banner from "../components/shared/banner";

const DISPLAY_FONT = "'Anton', 'Oswald', 'Arial Narrow', sans-serif";

const PROSE_CLASSES = `w-full text-lg leading-8 text-[#EDEAE1]
  [&_p]:mb-6
  [&_h2]:mb-4 [&_h2]:mt-14 [&_h2]:border-l-4 [&_h2]:border-[#D0182B] [&_h2]:pl-4 [&_h2]:font-['Anton',_'Oswald',_sans-serif] [&_h2]:text-2xl [&_h2]:uppercase [&_h2]:leading-tight
  [&_h2]:tracking-tight
  [&_h3]:mb-3 [&_h3]:mt-10 [&_h3]:font-['Anton',_'Oswald',_sans-serif] [&_h3]:text-xl [&_h3]:uppercase [&_h3]:tracking-tight
  [&_a]:text-[#D0182B] [&_a]:underline [&_a]:underline-offset-4 [&_a]:decoration-2
  [&_ul]:mb-6 [&_ul]:ml-6 [&_ul]:list-disc [&_ol]:mb-6 [&_ol]:ml-6 [&_ol]:list-decimal [&_li]:mb-2
  [&_blockquote]:my-8 [&_blockquote]:border-l-4 [&_blockquote]:border-[#D0182B] [&_blockquote]:bg-[#141414] [&_blockquote]:py-4 [&_blockquote]:pl-6 [&_blockquote]:pr-4 [&_blockquote]:text-xl [&_blockquote]:font-bold [&_blockquote]:not-italic [&_blockquote]:leading-snug [&_blockquote]:text-[#EDEAE1]
  [&_img]:my-8 [&_img]:h-auto [&_img]:w-full [&_img]:border [&_img]:border-[#2A2A2A]
  [&_figure]:my-8 [&_figcaption]:mt-2 [&_figcaption]:text-center [&_figcaption]:text-sm [&_figcaption]:text-[#8C8880]
  [&_hr]:my-10 [&_hr]:border-[#2A2A2A]`;

// Splits the article's HTML on each <h2> section boundary, then weaves the
// banner images in between sections at evenly spaced points, instead of
// dumping them all after the content.
const injectBanners = (html, banners) => {
  if (!banners.length) return [{ type: "html", html }];

  const sections = html.split(/(?=<h2)/);
  const gap = Math.max(1, Math.floor(sections.length / (banners.length + 1)));

  const blocks = [];
  let bannerIdx = 0;

  sections.forEach((section, i) => {
    blocks.push({ type: "html", html: section });

    const isLastSection = i === sections.length - 1;
    if (bannerIdx < banners.length && (i + 1) % gap === 0 && !isLastSection) {
      blocks.push({ type: "banner", src: banners[bannerIdx] });
      bannerIdx += 1;
    }
  });

  // Any banners left over (e.g. more banners than gaps) go in before the end.
  while (bannerIdx < banners.length) {
    blocks.splice(blocks.length - 1, 0, {
      type: "banner",
      src: banners[bannerIdx],
    });
    bannerIdx += 1;
  }

  return blocks;
};

const useArticleSEO = (article) => {
  useEffect(() => {
    if (!article) return;

    document.title = `${article.title} | onpointcombat `;

    const setMeta = (name, content, isProperty = false) => {
      const attribute = isProperty ? "property" : "name";
      let element = document.querySelector(`meta[${attribute}="${name}"]`);

      if (!element) {
        element = document.createElement("meta");
        element.setAttribute(attribute, name);
        document.head.appendChild(element);
      }

      element.setAttribute("content", content);
    };

    setMeta("description", article.excerpt || article.title);
    setMeta("og:title", article.title, true);
    setMeta("og:description", article.excerpt || article.title, true);
    setMeta("og:type", "article", true);

    if (article?.imageUrl) {
      setMeta("og:image", article?.imageUrl, true);
    }

    setMeta("twitter:card", "summary_large_image");

    let schema = document.getElementById("article-schema");

    if (!schema) {
      schema = document.createElement("script");
      schema.id = "article-schema";
      schema.type = "application/ld+json";
      document.head.appendChild(schema);
    }

    schema.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "NewsArticle",
      headline: article.title,
      image: article.imageUrl ? [article.imageUrl] : [],
      datePublished: article.publishedAt,
      author: [
        {
          "@type": "Person",
          name: article?.author,
        },
      ],
      publisher: {
        "@type": "Organization",
        name: article.publisher,
      },
    });

    return () => {
      schema?.remove();
    };
  }, [article]);
};

const NotFound = () => (
  <main className="flex min-h-screen items-center justify-center bg-[#0A0A0A] px-6 text-[#EDEAE1]">
    <div className="max-w-md  text-center">
      <p
        style={{ fontFamily: DISPLAY_FONT }}
        className="mb-2 text-sm uppercase tracking-[0.2em] text-[#D0182B]"
      >
        Missing From The Card
      </p>

      <h1
        style={{ fontFamily: DISPLAY_FONT }}
        className="mb-4 text-4xl uppercase leading-none"
      >
        Article Not Found
      </h1>

      <p className="mb-8 text-[#8C8880]">
        The report you're looking for got pulled from the lineup.
      </p>

      <Link
        to="/"
        style={{ fontFamily: DISPLAY_FONT }}
        className="inline-block border-2 border-[#D0182B] px-6 py-3 text-sm uppercase tracking-wide text-[#D0182B] transitionAll hover:bg-[#D0182B] hover:text-[#0A0A0A]"
      >
        ← Back to All News
      </Link>
    </div>
  </main>
);

const BlogArticle = () => {
  const { slug } = useParams();

  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    const loadArticle = async () => {
      setLoading(true);
      setNotFound(false);
      setArticle(null);

      try {
        const manualArticle = manualBlogs.find((blog) => blog?.slug === slug);
        if (manualArticle) {
          setArticle(manualArticle);
          return;
        }

        const apiArticle = await fetchArticleBySlug(slug);
        if (!apiArticle) {
          setNotFound(true);
          return;
        }

        setArticle({
          ...apiArticle,
          id: apiArticle.id || apiArticle._id,
          slug: apiArticle.slug,
          title: apiArticle.title,
          excerpt:
            apiArticle.description ||
            apiArticle.contentText?.slice(0, 180) ||
            "",
          imageUrl: apiArticle.image || null,
          publishedAt: apiArticle.publishedDate,
          publisher: apiArticle.publisher,
          content: apiArticle.content || "",
          contentText: apiArticle.contentText || "",
          sourceUrl: apiArticle.sourceUrl,
          source: apiArticle.source,
          author: apiArticle.author || apiArticle.publisher,
        });
      } catch (error) {
        console.error("Failed to load article:", error);
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    };

    loadArticle();
  }, [slug]);

  useArticleSEO(article);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (loading) {
    return (
      <>
        <Header />
        <BlogSkel />
      </>
    );
  }

  if (notFound || !article) {
    return <NotFound />;
  }

  const banners = [
    article.bannerUrl1,
    article.bannerUrl2,
    article.bannerUrl3,
  ].filter(Boolean);

  const contentBlocks = injectBanners(article.content, banners);

  return (
    <>
      <Header />
      <main className="min-h-screen bg-[#050505] font-sans text-[#EDEAE1]">
        <article className="mx-auto max-w-6xl px-6 py-12 md:py-16">
          <header className="mb-10 border-b-4 border-[#D0182B] pb-8">
            <div className="mb-5 flex items-center gap-3 text-sm font-bold uppercase tracking-[0.1em] text-[#D0182B]">
              {article?.publisher !== "" && (
                <>
                  <span>{article.publisher}</span>
                  <span className="h-3 w-[2px] bg-[#8C8880]" />
                </>
              )}
              <span className="text-[#8C8880]">
                {formatDate(article.publishedAt)}
              </span>
            </div>

            <h1
              style={{ fontFamily: DISPLAY_FONT }}
              className="mb-6 text-5xl uppercase leading-[0.92] tracking-tight text-[#EDEAE1] md:text-7xl"
            >
              {article.title}
            </h1>

            {article?.author !== "" && (
              <span className="text-sm font-medium text-[#8C8880]">
                by{" "}
                <span className="font-bold text-[#EDEAE1]">
                  {article?.author}
                </span>
              </span>
            )}
          </header>

          {article?.imageUrl && (
            <div className="relative mb-12 h-[250px] sm:h-[600px] md:h-[900px]  overflow-hidden">
              <img
                src={article.imageUrl}
                alt={article.title}
                className="block h-full w-full object-cover object-top"
              />
            </div>
          )}

          <div className={PROSE_CLASSES}>
            {contentBlocks.map((block, idx) =>
              block.type === "banner" ? (
                <a
                  href="https://www.trilfi.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="my-10 block overflow-hidden transitionAll ease-in-out cursor-pointer hover:opacity-90"
                >
                  <img
                    src={block.src}
                    alt=""
                    className="block h-auto w-full object-cover"
                  />
                </a>
              ) : (
                <div
                  key={`html-${idx}`}
                  dangerouslySetInnerHTML={{ __html: block.html }}
                />
              )
            )}
          </div>

          <Banner seed={slug} className="my-12" />

          <div className="text-[var(--foreground-muted)] font-medium">
            Source:{" "}
            <a
              target="_blank"
              rel="noopener noreferrer"
              className="underline text-[#D0182B] hover:text-[var(--primary)] transitionAll"
              href={article?.sourceUrl}
            >
              Low Kick MMA
            </a>
          </div>

          {article?.author && ( <About className="mt-12" label={article?.publisher} title={article?.author}/>)}

          <footer className="border-t border-[var(--borders)] my-16">
            <section className="relative my-16 overflow-hidden border border-[var(--bg-lighter)] bg-[var(--bg-light)] px-6 py-10 text-center">
              <div className="pointer-events-none absolute -right-24 -top-24 h-64 w-64 rounded-full bg-[var(--primary)] opacity-10 blur-3xl" />
              <div className="pointer-events-none absolute -bottom-24 -left-24 h-64 w-64 rounded-full bg-[var(--primary)] opacity-10 blur-3xl" />

              <div className="relative z-10 mx-auto max-w-2xl">
                <span
                  style={{ fontFamily: DISPLAY_FONT }}
                  className="mb-4 inline-block text-xs font-semibold uppercase tracking-[0.15em] text-[var(--primary-muted)]"
                >
                  Every Angle. Every Fighter. No Filter.
                </span>

                <h2
                  style={{ fontFamily: DISPLAY_FONT }}
                  className="mb-4 text-2xl font-normal leading-tight md:text-3xl"
                >
                  ONPOINT COMBAT
                </h2>

                <p className="mx-auto mb-7 max-w-3xl text-base leading-relaxed text-[#8C8880]">
                  On Point Combat is a sports media, commerce and entertainment
                  platform dedicated entirely to combat sports. We provide a
                  global audience of fight fans with MMA, boxing, muay thai, and
                  wrestling coverage, featuring live scorecards, breaking news,
                  viral highlights, and commentary. The platform also features
                  an integrated storefront offering officially licensed fighter
                  merchandise.
                </p>

                <Link to="/" style={{ fontFamily: DISPLAY_FONT }}
                  className="inline-flex items-center border-2 border-[#D0182B] bg-[#D0182B] px-5 py-2.5 text-sm font-bold uppercase tracking-wide text-[#EDEAE1] transitionAll hover:bg-transparent hover:text-[#D0182B]">
                  Explore
                  <span className="ml-1">
                    <RxCaretRight />
                  </span>
                </Link>
              </div>
            </section>
          </footer>
        </article>
      </main>
    </>
  );
};

export default BlogArticle;
